/**
 * 1.8.5 §3–§5 (D-1.8.5-3, -4, -5) — the Tech Support visit, from the ticket email to the report:
 *
 *   New            a ticket is "New" until its tech opens it (`openedAt` on the ticket's app file); the office's Today
 *                  says "New Tech Support tickets not opened yet (n)".
 *   Not home       at the door: no entry, ever. The ticket goes back to the office as "Needs a new day" (an
 *                  SM_FollowUpActions row, the same columns and choice values as Housekeeping's — shared/followups.ts);
 *                  the tenant is never marked No Show by it.
 *   The summary    the after-visit summary is what the tech approved in the box; "Help me write it" only suggests.
 *   The report     each closed ticket is a report: searchable by month, device and help kind; one page; Print and PDF.
 *
 * No dependencies beyond the shared rules, so the app and the API share it.
 */
import type { TranslatedText } from './api-types';
import { HELP_KEYS, type HelpKey, type TechOutcome, type TechPhoto, type WhyKey } from './tech';

/** Elijah's note above the box, word for word (10 Oct 2026). The screen's words come from i18n (visit185.summaryNote). */
export const SUMMARY_NOTE_EN = 'This is your after-visit summary. Please keep it simple, detailed and factual.';

/** Admin → Settings → Tech Support: "Email the tech about new tickets" (Yes/No; no row = Yes). */
export const TICKET_EMAIL_SETTING = 'TECH_TICKET_EMAIL_ON';

/** The window event the Tech Wizard sends from "Add these steps to my summary". */
export const WIZARD_STEPS_EVENT = 'abf:wizard-steps';
export interface WizardStepsDetail {
  appointmentId: string;
  text: string;
}

/** The longest notes "Help me write it" sends (found, done, next and the Wizard's steps together). */
export const SUMMARY_INPUT_MAX = 6000;

/* ------------------------------------------------------------------------------------------------
 * Never a name, an apartment, a phone or an email to the AI
 * ---------------------------------------------------------------------------------------------- */

const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * The notes with the tenant's name (each part of it), their apartment, and every phone number and email address
 * replaced by a plain placeholder. Run on what is sent to the AI, and again on what comes back.
 */
export function stripPersonal(text: string, who: { names: readonly (string | null | undefined)[]; unit?: string | null }): string {
  let s = text;
  s = s.replace(/[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g, '[email]');
  s = s.replace(/(?:\+?1[\s.-]?)?\(?\b\d{3}\)?[\s.-]?\d{3}[\s.-]\d{4}\b/g, '[phone]');
  const unit = (who.unit ?? '').trim();
  if (unit) {
    s = s.replace(new RegExp(`\\b(?:apartment|apt\\.?|unit|apartamento|apto\\.?)\\s*#?\\s*${escapeRe(unit)}\\b`, 'gi'), '[apartment]');
    s = s.replace(new RegExp(`(^|[^A-Za-z0-9-])${escapeRe(unit)}(?![A-Za-z0-9])`, 'gi'), '$1[apartment]');
  }
  const parts = new Set<string>();
  for (const n of who.names) {
    const full = (n ?? '').trim();
    if (!full) continue;
    parts.add(full);
    for (const p of full.split(/[\s,]+/)) if (p.replace(/[^\p{L}]/gu, '').length >= 2) parts.add(p);
  }
  // longest first, so "Maria Lopez" goes whole before "Maria"
  for (const p of [...parts].sort((a, b) => b.length - a.length)) {
    s = s.replace(new RegExp(`(^|[^\\p{L}\\p{N}])${escapeRe(p)}(?![\\p{L}\\p{N}])`, 'giu'), '$1[name]');
  }
  return s;
}

/** The notes "Help me write it" turns into a draft, as one text (labelled, in the order of the sheet). */
export function summaryNotes(n: { found?: string; done?: string; next?: string; steps?: string }): string {
  const parts: string[] = [];
  if (n.found?.trim()) parts.push(`What I found: ${n.found.trim()}`);
  if (n.done?.trim()) parts.push(`What I did: ${n.done.trim()}`);
  if (n.next?.trim()) parts.push(`What comes next: ${n.next.trim()}`);
  if (n.steps?.trim()) parts.push(`Steps I followed: ${n.steps.trim()}`);
  return parts.join('\n').slice(0, SUMMARY_INPUT_MAX);
}

/** The fixed instruction for "Help me write it". Nothing about the person or the ticket is ever added to it. */
export const SUMMARY_SYSTEM_PROMPT = [
  'You help a technology support associate write a short after-visit summary of a home tech support visit.',
  'Use only the notes given. Do not invent anything.',
  'Write three to six short, plain sentences: what was wrong, what was done, how it ended, and what comes next.',
  'Keep it simple, detailed and factual.',
  'Never add a name, an apartment number, a phone number or an email address; keep placeholders such as [name] or [phone] as they are.',
  'Write in the language of the notes. Output only the summary — no heading, no preface.',
].join(' ');

/* ------------------------------------------------------------------------------------------------
 * The office's Tickets: every ticket, grouped by tech
 * ---------------------------------------------------------------------------------------------- */

export interface OfficeTicket {
  appointmentId: string;
  dayKey: string;
  minutes: number;
  endMinutes: number;
  help: HelpKey[];
  other: string;
  status: string;
  confirmation: string;
  isNew: boolean;
  notHome: boolean;
  closed: boolean;
  outcome: TechOutcome | null;
  /** Not for the viewer (titles only). */
  tenantFirst: string | null;
  tenantName: string | null;
  unit: string | null;
}

export interface OfficeTicketGroup {
  /** null: "Not assigned" (no ticket address was set when it was booked). */
  techEmail: string | null;
  label: string;
  tickets: OfficeTicket[];
}

export interface OfficeTicketsResponse {
  todayKey: string;
  from: string;
  to: string;
  /** false for the viewer: no name, apartment or Open ticket. */
  names: boolean;
  groups: OfficeTicketGroup[];
}

/** How many days back the office's Tickets reach (the last few days), and ahead (the booking window). */
export const OFFICE_TICKETS_BACK_DAYS = 7;
export const OFFICE_TICKETS_AHEAD_DAYS = 42;

/** Groups by the ticket's tech (case-insensitive address), "Not assigned" last; tickets by day and time. */
export function groupByTech<T extends { techEmail?: string | null; dayKey: string; minutes: number }>(tickets: readonly T[], label: (email: string) => string, notAssigned: string): { techEmail: string | null; label: string; tickets: T[] }[] {
  const by = new Map<string, { techEmail: string | null; label: string; tickets: T[] }>();
  for (const t of tickets) {
    const email = (t.techEmail ?? '').trim();
    const key = email.toLowerCase();
    const g = by.get(key) ?? { techEmail: email || null, label: email ? label(email) : notAssigned, tickets: [] };
    g.tickets.push(t);
    by.set(key, g);
  }
  const groups = [...by.values()];
  for (const g of groups) g.tickets.sort((a, b) => a.dayKey.localeCompare(b.dayKey) || a.minutes - b.minutes);
  return groups.sort((a, b) => (a.techEmail === null ? 1 : b.techEmail === null ? -1 : a.label.localeCompare(b.label)));
}

/* ------------------------------------------------------------------------------------------------
 * Reports: each closed ticket
 * ---------------------------------------------------------------------------------------------- */

export interface TechReportRow {
  appointmentId: string;
  dayKey: string;
  minutes: number;
  help: HelpKey[];
  other: string;
  device: string;
  devices: HelpKey[];
  devicesOther: string;
  outcome: TechOutcome;
  /** The tenant's first name (the full name for the administrator and the co-administrator). */
  tenantFirst: string;
  tenantName: string | null;
  techEmail: string | null;
  closedBy: string;
  closedAt: string;
}

export interface TechReportsQuery {
  /** yyyyMM */
  month: string;
  device?: string;
  help?: HelpKey | '';
}

export interface TechReportsResponse {
  month: string;
  /** The months that can be searched (this one and the eleven before), newest first. */
  months: string[];
  reports: TechReportRow[];
}

export const isMonthKey = (m: unknown): m is string => typeof m === 'string' && /^\d{4}(0[1-9]|1[0-2])$/.test(m);

/** The search: a device's words anywhere in the device named at booking or the close-out's "other"; a help kind in what they asked for or what was worked on. */
export function matchesReport(r: Pick<TechReportRow, 'device' | 'devicesOther' | 'help' | 'devices' | 'other'>, q: Omit<TechReportsQuery, 'month'>): boolean {
  const words = (q.device ?? '').trim().toLowerCase();
  if (words) {
    const hay = `${r.device} ${r.devicesOther} ${r.other}`.toLowerCase();
    if (!words.split(/\s+/).every((w) => hay.includes(w))) return false;
  }
  if (q.help && (HELP_KEYS as readonly string[]).includes(q.help) && !r.help.includes(q.help) && !r.devices.includes(q.help)) return false;
  return true;
}

/** The twelve months up to and including the given one, newest first (yyyyMM). */
export function lastMonths(todayKey: string, n = 12): string[] {
  let y = Number(todayKey.slice(0, 4));
  let m = Number(todayKey.slice(4, 6));
  const out: string[] = [];
  for (let i = 0; i < n; i++) {
    out.push(`${y}${String(m).padStart(2, '0')}`);
    m--;
    if (m === 0) {
      m = 12;
      y--;
    }
  }
  return out;
}

export interface TechReportView {
  appointmentId: string;
  dayKey: string;
  minutes: number;
  endMinutes: number;
  place: string;
  tenantFirst: string;
  tenantName: string | null;
  unit: string | null;
  help: HelpKey[];
  other: string;
  device: string;
  /** What the tenant said at booking (what was wrong, in their words). */
  description: string;
  techEmail: string | null;
  arrivedAt: string | null;
  closeout: {
    devices: HelpKey[];
    devicesOther: string;
    found: string;
    done: string;
    next: string;
    summary: string;
    outcome: TechOutcome;
    why: WhyKey | null;
    whyOther: string;
    wouldResolve: string;
    followUp: { needed: boolean; what: string; who: string };
    finishedAt: string;
    tenantPresent: boolean;
    tenantCanRepeat: boolean;
    by: string;
    at: string;
  };
  photos: { before: TechPhoto[]; after: TechPhoto[] };
  /** The office's English beside a tech's words in another language (never written into a SharePoint column). */
  words?: Partial<Record<'summary' | 'found' | 'done' | 'next' | 'description', TranslatedText>>;
}
